/** Возрастной порог: спросить до того, как показать товар.
 *
 * Экран появляется не сразу, а после заставки — иначе человек отвечает на
 * вопрос, ещё не поняв, куда попал. Каталог за ним не прячется совсем: он
 * уходит за матовое стекло, и видно, что там есть что смотреть. Это и есть
 * смысл подтверждения — не замок, а честный вопрос на пороге.
 */
import { useState } from 'react';
import { request } from '@/shared/api';
import type { DisclaimerGate } from '@/shared/types';
import { Button } from '@/shared/ui';
import { tap } from '@/shared/tg';

export function Disclaimer({
  gate,
  onAnswer,
}: {
  gate: DisclaimerGate;
  onAnswer: (agreed: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);

  const answer = async (agreed: boolean) => {
    if (busy) return;
    setBusy(true);
    tap(agreed ? 'success' : 'warning');
    // Ответ уходит на сервер, но ждать его человеку незачем: решение принято,
    // и держать порог из-за медленной сети было бы издевательством
    request('/api/disclaimer', { agreed }).catch(() => undefined);
    onAnswer(agreed);
  };

  return (
    <div className="gate-veil">
      <div className="gate-card glass wet-edge layer-shadow w-full max-w-[380px] rounded-layer p-6 text-center backdrop-blur-xl">
        <div className="gate-mark mx-auto grid size-16 place-items-center rounded-full border border-danger/40 bg-danger/10 text-xl font-[760] text-danger">
          18+
        </div>

        <p className="mt-5 mb-6 text-[15px] leading-relaxed whitespace-pre-wrap text-frost">
          {gate.text}
        </p>

        <div className="grid gap-2.5">
          <Button disabled={busy} onClick={() => answer(true)}>
            {gate.accept}
          </Button>
          <Button tone="ghost" disabled={busy} onClick={() => answer(false)}>
            {gate.decline}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Отказался — витрина закрывается. В следующий раз спросим снова. */
export function GateClosed({ gate }: { gate: DisclaimerGate }) {
  return (
    <div className="gate-veil">
      <div className="gate-card w-full max-w-[380px] rounded-layer p-6 text-center">
        <div className="mx-auto grid size-16 place-items-center rounded-full border border-glass-line bg-glass text-2xl">
          🚫
        </div>
        <h1 className="mt-5 text-lg font-[680] tracking-[-.02em]">Каталог закрыт</h1>
        <p className="mt-2 text-sm text-hint">
          Раздел доступен только совершеннолетним. Если возраст указан по ошибке —
          закройте окно и откройте витрину заново.
        </p>
        <p className="mt-4 text-xs text-hint/70">{gate.decline}</p>
      </div>
    </div>
  );
}
